import { Disclosure, DisclosurePanel } from '@headlessui/react'
import { DisclosureButton } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'

import { InnerTagStyledDiv, NavItemStyledDiv, OuterTagStyledDiv } from './StyledDivs'
import { ApiSubNavMessage } from '../ApiSubNavMessage'
import { ApiSubNavSchema } from '../ApiSubNavSchema'
import { ApiSubNavServer } from '../ApiSubNavServer'
import { ApiSubNavOperation } from '~/portal/components/ConsumerSideNavigation/ApiNavItem/ApiSubNavOperation'
import { API_TYPE, AsyncApiMessage, AsyncApiServer, Operation, Schema } from '~/portal/types/content'

export const ApiSubNav = ({
  tag,
  operations,
  schemas,
  asyncApiMessages,
  asyncApiServers,
  active = false,
  parentPath,
  level,
  selectedItem,
  setSelectedItem,
  onClick,
  apiType,
}: {
  tag?: string,
  operations?: Operation[],
  schemas?: Schema[],
  asyncApiMessages?: AsyncApiMessage[],
  asyncApiServers?: AsyncApiServer[],
  active?: boolean,
  parentPath: string,
  level: number,
  selectedItem?: string | null,
  setSelectedItem: (selectedItem: string | null) => void,
  onClick?: (e) => void,
  apiType?: API_TYPE
}) => {
  const { t } = useTranslation()

  return (
    <Disclosure defaultOpen>
      {({ open }) => (
        <div className="text-body3 text-(--sp-sidenav-tag-color,var(--neutral-70)) dark:text-(--sp-sidenav-tag-color,var(--neutral-30))">
          <OuterTagStyledDiv>
            <div className={clsx(active && 'separator h-[7px]')} />
            <InnerTagStyledDiv active={active} level={level}>
              <DisclosureButton
                open={open}
                className={clsx(
                  '!justify-end flex-row-reverse gap-2 px-1.5',
                  active ? 'py-0.5 mb-[7px]' : 'py-[9px]',
                )}
              >
                <span className="pr-4 text-left">{tag || t('consumer.product.side.navigation.api.operations')}</span>
              </DisclosureButton>
            </InnerTagStyledDiv>
          </OuterTagStyledDiv>
          <DisclosurePanel>
            {operations?.map((operation, index) => (
              <NavItemStyledDiv
                lastItem={index === operations.length - 1}
                index={index}
                active={active}
                level={level}
                key={operation.operationId}
                operationsLength={operations.length}
                isSelected={(selectedItem === (operation.operationId + operation.path + operation.method)) && active}
              >
                <ApiSubNavOperation
                  operation={operation}
                  active={active}
                  parentPath={parentPath}
                  isSelected={(selectedItem === (operation.operationId + operation.path + operation.method)) && active}
                  setSelectedOperation={setSelectedItem}
                  onClick={onClick}
                />
              </NavItemStyledDiv>
            ))}
            {schemas?.map((schema, index) => {
              let isSelected
              if (apiType === API_TYPE.ASYNCAPI) {
                isSelected = (selectedItem === schema.name) && active
              } else {
                isSelected = (selectedItem === (schema.title ?? schema.name)) && active
              }
              return (
                <NavItemStyledDiv
                  lastItem={index === schemas.length - 1}
                  index={index}
                  active={active}
                  level={level}
                  key={schema.name}
                  operationsLength={schemas.length}
                  isSelected={isSelected}
                >
                  <ApiSubNavSchema
                    schema={schema}
                    active={active}
                    parentPath={parentPath}
                    isSelected={isSelected}
                    setSelectedSchema={setSelectedItem}
                    onClick={onClick}
                    apiType={apiType}
                  />
                </NavItemStyledDiv>
              )
            })}
            {asyncApiServers?.map((server, index) => (
              <NavItemStyledDiv
                lastItem={index === asyncApiServers.length - 1}
                index={index}
                active={active}
                level={level}
                key={server.serverId}
                operationsLength={asyncApiServers.length}
                isSelected={(selectedItem === server.serverId) && active}
              >
                <ApiSubNavServer
                  server={server}
                  active={active}
                  parentPath={parentPath}
                  isSelected={(selectedItem === server.serverId) && active}
                  setSelectedServer={setSelectedItem}
                  onClick={onClick}
                />
              </NavItemStyledDiv>
            ))}
            {asyncApiMessages?.map((message, index) => (
              <NavItemStyledDiv
                lastItem={index === asyncApiMessages.length - 1}
                index={index}
                active={active}
                level={level}
                key={message.messageId}
                operationsLength={asyncApiMessages.length}
                isSelected={(selectedItem === message.messageId) && active}
              >
                <ApiSubNavMessage
                  message={message}
                  active={active}
                  parentPath={parentPath}
                  isSelected={(selectedItem === message.messageId) && active}
                  setSelectedMessage={setSelectedItem}
                  onClick={onClick}
                />
              </NavItemStyledDiv>
            ))}
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  )
}